import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet";
import { v4 } from "uuid";
// import ReactFlagsSelect from "react-flags-select";
import axios from "axios";
import platform from "platform";

import Conversation from "./containers/Conversation";

import "./assets/scss/styles.scss";

// import SimplifiedSteps from "./config/simplified";
import FullSteps from "./config/full";
import ProtectionSteps from "./config/protection";
import CriteriaSteps from "./config/criteria";
import WealthSteps from "./config/wealth";
import LifeInsuranceSteps from "./config/life-insurance";
import BookingSteps from "./config/booking";

import { ConversationStep, Theme } from "./interfaces";
import GeneralInsuranceSteps from "./config/generalInsurance";
import RightmoveAutoResponder from "./config/rightmove";
import CSQuestionnaire from "./config/cs-questionnaire";
import ShortSteps from "./config/short";
import { initializeGoogleAnalytics } from "./util";
import { useAppDispatch } from "./store/hooks";
import { chatActions } from "./store/chat/slice";
import { analyticsActions } from "./store/analytics/slice";
import InsightKentFooter from "./components/client/InsightKent/Footer";
import { trackEvent } from "./store/analytics/action";
// import { selectIsConnected } from "./store/chat/selectors";
// import languages from "./assets/data/languages";

import ReactPixel from "react-facebook-pixel";

const { REACT_APP_SHOP_URL, REACT_APP_COMMS_URL, REACT_APP_TOKEN_PASSCODE } = process.env;
//
interface Props {
    apiKey: string;
    config: { hideAppBar: string; sessionId: string };
}

function App(props: Props) {
    const dispatch = useAppDispatch();
    // const navigate = useNavigate();
    const [isLoading, setIsLoading] = useState(true);
    // const [languageSelected, setLanguageSelected] = useState("GB");
    const [theme, setTheme] = useState<Theme>({
        apiKey: "",
        id: "ruby",
        themeId: "red",
        version: "",
        email: "",
        logo: "",
        customization: {},
        flowCustomization: "",
        appointmentCustomization: {},
        customFlowData: {},
        stripe: {},
        links: {},
        files: [],
        facebookPixelId: "",
        googleAnalyticsTrackingId: [],
        config: {},
    });
    const [stepList, setStepList] = useState<ConversationStep[]>([]);

    const switchTheme = (theme: Theme) => {
        setTheme(theme);
    };

    const switchFlow = (flow: string, themeData: Theme = theme): ConversationStep[] => {
        switchTheme(themeData);

        switch (flow) {
            case "protection":
                setStepList(ProtectionSteps(themeData));
                return ProtectionSteps(themeData);
            case "criteria":
                setStepList(CriteriaSteps(themeData));
                return CriteriaSteps(themeData);
            case "wealth":
                setStepList(WealthSteps(themeData));
                return WealthSteps(themeData);
            case "general-insurance":
                setStepList(GeneralInsuranceSteps(themeData));
                return GeneralInsuranceSteps(themeData);
            case "rightmove":
                setStepList(RightmoveAutoResponder(themeData));
                return RightmoveAutoResponder(themeData);
            case "customer-service-questionnaire":
                setStepList(CSQuestionnaire(themeData));
                return CSQuestionnaire(themeData);
            case "short":
                setStepList(ShortSteps(themeData));
                return ShortSteps(themeData);
            case "life-insurance":
                setStepList(LifeInsuranceSteps(themeData));
                return LifeInsuranceSteps(themeData);
            case "booking":
                setStepList(BookingSteps(themeData));
                return BookingSteps(themeData);
            default:
                setStepList(FullSteps(themeData));
                return FullSteps(themeData);
        }
    };

    const getProductData = async () => {
        try {
            if (props.apiKey) {
                const apiKey = props.apiKey;
                const sessionId = props.config.sessionId;
                const { data } = await axios.get(
                    `${REACT_APP_SHOP_URL}/api/v1/shop/client/get?apiKey=${apiKey}`
                );

                const {
                    email,
                    logo,
                    name,
                    attributes: { theme: themeId, version },
                    emailCustomization,
                    stripe,
                    products,
                    links,
                    workHours,
                    files,
                    facebookPixelId,
                    googleAnalyticsTrackingId,
                    appointmentCustomization,
                } = data;

                dispatch(chatActions.setIsLivechatEnabled({ apiKey }));

                let theme = "",
                    primaryColor = "",
                    secondaryColor = "";

                // const tokenRequest = await axios.post(
                //     REACT_APP_COMMS_URL + "/api/requestJwtToken/27tec",
                //     { apiKey },
                //     {
                //         headers: {
                //             Authorization: `Bearer ${REACT_APP_TOKEN_PASSCODE}`,
                //         },
                //         // withCredentials: true,
                //     }
                // );
                // if (tokenRequest) {
                //     const parsedToken = tokenRequest.data.body;
                //     const token = parsedToken?.token;
                //     sessionStorage.setItem("token", token);
                // }

                let customFlowData, customData;
                if (data?.flowCustomization) {
                    customFlowData = await axios.get(
                        `${REACT_APP_SHOP_URL}/api/v1/shop/flow?id=${data?.flowCustomization}`
                    );
                }

                if (sessionId) {
                    customData = await axios.get(
                        `${REACT_APP_SHOP_URL}/api/v1/shop/client/data?apiKey=${apiKey}&sessionId=${sessionId}`
                    );
                }

                let themeProps: Theme = {
                    apiKey,
                    themeId,
                    email,
                    logo,
                    name,
                    version,
                    config: data?.config || {},
                    customization: {
                        companyName: data?.customization?.companyName || "Meet Parker",
                        botAvatar: data?.customization?.botAvatar || logo,
                        triggers: data?.customization?.triggers || [],
                        expertPhoneNumber: data?.customization?.expertPhoneNumber || "",
                        switchToMortgageText: data?.customization?.switchToMortgageText || "",
                        criteria: data?.customization?.criteria || {},
                        templates: data?.customization?.templates || {},
                        botName: data?.customization?.botName || "Parker",
                        fontFamily: data?.customization?.fontFamily,
                    },
                    emailCustomization,
                    appointmentCustomization,
                    flowCustomization: data?.flowCustomization,
                    customFlowData: customFlowData?.data,
                    customData: customData?.data || {},
                    stripe,
                    products,
                    files,
                    links,
                    facebookPixelId,
                    workHours,
                    googleAnalyticsTrackingId,
                };

                switch (themeId) {
                    case "blue":
                        theme = "royal";
                        primaryColor = "#002554";
                        secondaryColor = "#fdf8f3";
                        break;
                    case "brown":
                        theme = "hazelnut";
                        primaryColor = "#b1a296";
                        secondaryColor = "#dff1ff";
                        break;
                    case "red":
                        theme = "ruby";
                        primaryColor = "#ff385c";
                        secondaryColor = "#464646";
                        break;
                    case "green":
                        theme = "jade";
                        primaryColor = "#41b3a3";
                        secondaryColor = "#fff0e6";
                        break;
                    default:
                        throw new Error("Invalid product");
                }
                themeProps = {
                    ...themeProps,
                    id: theme,
                    customization: {
                        ...themeProps.customization,
                        primaryColor: data?.customization?.primaryColor || primaryColor,
                        secondaryColor: data?.customization?.secondaryColor || secondaryColor,
                    },
                };
                if (Array.isArray(googleAnalyticsTrackingId) && googleAnalyticsTrackingId.length) {
                    initializeGoogleAnalytics(googleAnalyticsTrackingId);
                }

                if (themeProps?.facebookPixelId) {
                    ReactPixel.init(themeProps?.facebookPixelId, undefined, {
                        autoConfig: true,
                        debug: true,
                    });
                    ReactPixel.pageView();
                }

                dispatch(
                    trackEvent({
                        pageVisit: {
                            deviceBrowser: platform.name,
                            deviceMobile: platform.product,
                            deviceOs: platform.os?.toString(),
                        },
                    })
                );
                setTheme(themeProps);
                switchFlow(version, themeProps);
            } else {
                throw new Error("Invalid Client.");
            }
            setIsLoading(false);
        } catch (err) {
            console.log("err", err);
            //navigate("/404");
            setIsLoading(false);
        }
    };

    const renderCustomComponents = () => {
        if (theme?.apiKey === "6fda33d3-466e-4f97-b80b-9e55f7a7d1f2") {
            return <InsightKentFooter theme={theme} />;
        }

        return null;
    };

    const renderPage = () => (
        <section className={`page product-${theme?.id}`}>
            {props?.config?.hideAppBar === "yes" ? null : (
                <div className="page-toolbar">
                    <img src={theme?.logo} alt={`product-${theme?.id}-logo`} height={80} />
                </div>
            )}
            <div
                className={`page-content ${
                    props?.config?.hideAppBar === "yes" ? "max-height" : ""
                }`}
            >
                <Conversation
                    theme={theme}
                    switchTheme={switchTheme}
                    switchFlow={switchFlow}
                    steps={stepList}
                />
            </div>
            <div className="page-background">
                <div className="page-cloud-1" />
                <div className="page-airplane" />
                <div className="page-cloud-2" />
            </div>
            {renderCustomComponents()}
        </section>
    );

    useEffect(() => {
        dispatch(chatActions.startConnecting());
        dispatch(
            analyticsActions.initializeAnalytics({
                sessionId: v4()!,
                apiKey: props?.apiKey!,
            })
        );
        getProductData();

        /*eslint-disable react-hooks/exhaustive-deps*/
    }, []);

    return isLoading ? null : (
        <>
            {/* <Helmet>
                <meta name="theme-color" content={theme?.customization?.primaryColor} />
            </Helmet> */}
            <style>{`\
        ${
            theme?.customization?.fontFamily
                ? `
          .page.product-${theme?.id} {
              font-family: ${theme?.customization?.fontFamily} !important;
          }     
        `
                : ""
        }
      `}</style>
            {renderPage()}
        </>
    );
}

export default App;
